
import React, { useState } from 'react'
import { Bell, X, Eye, CheckCircle } from 'lucide-react'
import { useOrcamentos } from '../hooks/useOrcamentos'

const NotificationBell: React.FC = () => {
  const { notificacoes, markNotificationAsRead } = useOrcamentos()
  const [isOpen, setIsOpen] = useState(false)

  const unreadNotifications = notificacoes.filter(n => !n.lida)
  const unreadCount = unreadNotifications.length

  const handleMarkAsRead = async (notificationId: string) => {
    await markNotificationAsRead(notificationId)
  }

  const handleMarkAllAsRead = async () => {
    for (const notification of unreadNotifications) {
      await markNotificationAsRead(notification._id)
    }
  }

  const getNotificationIcon = (tipo: string) => {
    switch (tipo) {
      case 'lead_fechado':
        return '🎯'
      case 'orcamento_status':
        return '📋'
      case 'orcamento_expirado':
        return '⏰'
      default:
        return '🔔'
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))
    
    if (diffInHours < 1) return 'Agora mesmo'
    if (diffInHours < 24) return `${diffInHours}h atrás`
    
    const diffInDays = Math.floor(diffInHours / 24)
    if (diffInDays < 7) return `${diffInDays}d atrás`
    
    return date.toLocaleDateString('pt-BR')
  }

  return (
    <div className="relative">
      {/* Bell Icon */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg"
      >
        <Bell size={24} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center font-semibold">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              Notificações
              {unreadCount > 0 && (
                <span className="ml-2 text-sm text-blue-600">({unreadCount} não lidas)</span>
              )}
            </h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-sm text-blue-600 hover:text-blue-800"
                  title="Marcar todas como lidas"
                >
                  <CheckCircle size={16} />
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="max-h-80 overflow-y-auto">
            {notificacoes.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <Bell size={48} className="mx-auto mb-2 text-gray-300" />
                <p>Nenhuma notificação</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notificacoes
                  .sort((a, b) => new Date(b.dataNotificacao).getTime() - new Date(a.dataNotificacao).getTime())
                  .map((notification) => (
                    <div
                      key={notification._id}
                      className={`p-4 hover:bg-gray-50 transition-colors ${
                        !notification.lida ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="text-2xl">
                          {getNotificationIcon(notification.tipo)}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h4 className={`text-sm font-medium ${
                              !notification.lida ? 'text-gray-900' : 'text-gray-700'
                            }`}>
                              {notification.titulo}
                            </h4>
                            {!notification.lida && (
                              <button
                                onClick={() => handleMarkAsRead(notification._id)}
                                className="text-blue-600 hover:text-blue-800 ml-2"
                                title="Marcar como lida"
                              >
                                <Eye size={14} />
                              </button>
                            )}
                          </div>
                          
                          <p className={`text-sm mt-1 ${
                            !notification.lida ? 'text-gray-700' : 'text-gray-500'
                          }`}>
                            {notification.mensagem}
                          </p>
                          
                          <p className="text-xs text-gray-400 mt-2">
                            {formatDate(notification.dataNotificacao)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notificacoes.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setIsOpen(false)}
                className="w-full text-center text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Ver todas as notificações
              </button>
            </div>
          )}
        </div>
      )}

      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  )
}

export default NotificationBell
